import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Pencil, Plus, RotateCcw, Trash2, XCircle } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import AdminModal from '../../components/AdminModal';
import FieldList from '../../components/FieldList';
import AllFieldsSection from '../../components/AllFieldsSection';
import EntityAuditList from '../../components/EntityAuditList';
import StatusBadge from '../../components/StatusBadge';
import SearchSelect, { SearchOption } from '../../components/SearchSelect';
import { formatDateTime } from '../../lib/format';
import { searchActiveUsers } from '../../lib/pickers';

interface WorkspaceDetail {
  workspace: Record<string, unknown> & {
    id: string;
    name: string;
    description: string | null;
    owner_org_id: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    organization_name: string | null;
    organization_is_active: boolean | null;
  };
  members: Array<{
    member_id: string;
    role: string;
    status: string;
    user_id: string;
    display_name: string | null;
    username: string | null;
    user_is_active: boolean;
    email: string | null;
  }>;
  invitations: Array<{
    id: string;
    email: string;
    status: string;
    created_at: string;
  }>;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'members', label: 'Members' },
  { key: 'activity', label: 'Activity' },
];

const AccountWorkspaceDetailPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchOption | null>(null);
  const [memberRole, setMemberRole] = useState('contributor');
  const [removeMemberTarget, setRemoveMemberTarget] = useState<{ userId: string; memberName: string } | null>(null);
  const [cancelInvitationTarget, setCancelInvitationTarget] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['admin-accounts', 'workspace', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/admin/accounts/workspaces/${workspaceId}`);
      return response.data.data as WorkspaceDetail;
    },
    enabled: !!workspaceId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'workspace', workspaceId] });
    queryClient.invalidateQueries({ queryKey: ['admin-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'workspace', workspaceId ?? ''] });
  };

  const editMutation = useMutation({
    mutationFn: () =>
      api.put(`/admin/workspaces/${workspaceId}`, {
        name: editForm.name,
        description: editForm.description || null,
      }),
    onSuccess: () => {
      toast.success('Workspace updated');
      setEditOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to update workspace'),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.put(`/admin/workspaces/${workspaceId}/deactivate`),
    onSuccess: () => {
      toast.success('Workspace deactivated');
      setDeactivateOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to deactivate workspace'),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.put(`/admin/workspaces/${workspaceId}/reactivate`),
    onSuccess: () => {
      toast.success('Workspace reactivated');
      setReactivateOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to reactivate workspace'),
  });

  const addMemberMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/workspace-members', {
        workspaceId,
        userId: selectedUser?.id,
        role: memberRole,
      }),
    onSuccess: () => {
      toast.success('Member added');
      setSelectedUser(null);
      setMemberRole('contributor');
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to add member'),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (targetUserId: string) =>
      api.delete('/admin/workspace-members', { data: { workspaceId, userId: targetUserId } }),
    onSuccess: () => {
      toast.success('Member removed');
      setRemoveMemberTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to remove member'),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: (email: string) =>
      api.delete('/admin/email-invitations', { data: { workspaceId, email } }),
    onSuccess: () => {
      toast.success('Invitation removed');
      setCancelInvitationTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to remove invitation'),
  });

  if (detail.isLoading) {
    return (
      <AdminPageShell title="Workspace" backUrl="/admin-next/accounts?tab=workspaces">
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading workspace…</p>
      </AdminPageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AdminPageShell title="Workspace" backUrl="/admin-next/accounts?tab=workspaces">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn't load this workspace.</p>
          <Button variant="outline" size="sm" onClick={() => detail.refetch()}>
            Retry
          </Button>
        </div>
      </AdminPageShell>
    );
  }

  const { workspace, members, invitations } = detail.data;

  const openEdit = () => {
    setEditForm({ name: workspace.name ?? '', description: (workspace.description as string) ?? '' });
    setEditOpen(true);
  };

  return (
    <AdminPageShell
      title={workspace.name}
      description="Workspace"
      backUrl="/admin-next/accounts?tab=workspaces"
      actions={
        <>
          <Button variant="outline" size="sm" icon={Pencil} onClick={openEdit}>
            Edit
          </Button>
          {workspace.is_active ? (
            <Button variant="outline" size="sm" icon={XCircle} onClick={() => setDeactivateOpen(true)}>
              Deactivate
            </Button>
          ) : (
            <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setReactivateOpen(true)}>
              Reactivate
            </Button>
          )}
        </>
      }
    >
      <div className="flex items-center gap-2">
        <StatusBadge
          active={workspace.is_active}
          viaParent={workspace.is_active && workspace.organization_is_active === false}
        />
        {workspace.organization_name && (
          <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            owned by{' '}
            <Link
              to={`/admin-next/accounts/organizations/${workspace.owner_org_id}`}
              className="text-primary-700 hover:underline dark:text-primary-400"
            >
              {workspace.organization_name}
            </Link>
          </span>
        )}
      </div>

      <AdminTabs tabs={TABS} activeKey={tab} onChange={setTab} />

      {tab === 'overview' && (
        <div className="card space-y-4 p-5">
          <FieldList
            fields={[
              { label: 'ID', value: <span className="font-mono text-xs">{workspace.id}</span>, copyValue: workspace.id },
              { label: 'Name', value: workspace.name },
              { label: 'Description', value: (workspace.description as string) || '—' },
              {
                label: 'Owning Organization',
                value: (
                  <Link
                    to={`/admin-next/accounts/organizations/${workspace.owner_org_id}`}
                    className="text-primary-700 hover:underline dark:text-primary-400"
                  >
                    {workspace.organization_name ?? workspace.owner_org_id}
                  </Link>
                ),
                copyValue: workspace.owner_org_id,
              },
              {
                label: 'Status',
                value: (
                  <StatusBadge
                    active={workspace.is_active}
                    viaParent={workspace.is_active && workspace.organization_is_active === false}
                  />
                ),
              },
              { label: 'Created', value: formatDateTime(workspace.created_at) },
              { label: 'Updated', value: formatDateTime(workspace.updated_at) },
            ]}
          />
          <AllFieldsSection record={workspace} />
        </div>
      )}

      {tab === 'members' && (
        <div className="space-y-4">
          <div className="card p-4">
            <h4 className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              Add member
            </h4>
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[16rem] flex-1">
                <SearchSelect
                  placeholder="Search active users…"
                  fetchOptions={searchActiveUsers}
                  selected={selectedUser}
                  onSelect={setSelectedUser}
                  onClear={() => setSelectedUser(null)}
                  ariaLabel="Search users"
                />
              </div>
              <select
                value={memberRole}
                onChange={(event) => setMemberRole(event.target.value)}
                aria-label="Workspace role"
                className="h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              >
                <option value="owner">Owner</option>
                <option value="contributor">Contributor</option>
                <option value="guest">Guest</option>
              </select>
              <Button
                variant="primary"
                size="sm"
                icon={Plus}
                disabled={!selectedUser}
                loading={addMemberMutation.isLoading}
                onClick={() => addMemberMutation.mutate()}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="card p-5">
            {members.length === 0 ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No members.</p>
            ) : (
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {members.map((member) => (
                  <li key={member.member_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <div>
                      <Link
                        to={`/admin-next/accounts/users/${member.user_id}`}
                        className="font-medium text-primary-700 hover:underline dark:text-primary-400"
                      >
                        {member.display_name || member.username || member.email || member.user_id}
                      </Link>
                      <span className="ml-2 text-text-tertiary dark:text-text-dark-tertiary">{member.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-background-secondary px-2 py-0.5 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                        {member.role}
                      </span>
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{member.status}</span>
                      <StatusBadge active={member.user_is_active !== false} />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        onClick={() =>
                          setRemoveMemberTarget({
                            userId: member.user_id,
                            memberName: member.display_name || member.username || member.email || member.user_id,
                          })
                        }
                        ariaLabel={`Remove ${member.email || member.user_id}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {invitations.length > 0 && (
            <div className="card p-5">
              <h4 className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                Pending invitations
              </h4>
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {invitations.map((invitation) => (
                  <li key={invitation.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                    <span>{invitation.email}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        invited {formatDateTime(invitation.created_at)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={XCircle}
                        onClick={() => setCancelInvitationTarget(invitation.email)}
                        ariaLabel={`Cancel invitation for ${invitation.email}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'activity' && workspaceId && <EntityAuditList entityType="workspace" entityId={workspaceId} />}

      <AdminModal
        isOpen={editOpen}
        title="Edit Workspace"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={editForm.name.trim().length === 0}
              loading={editMutation.isLoading}
              onClick={() => editMutation.mutate()}
            >
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="edit-ws-name" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Name *
            </label>
            <input
              id="edit-ws-name"
              type="text"
              value={editForm.name}
              maxLength={255}
              onChange={(event) => setEditForm((current) => ({ ...current, name: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            />
          </div>
          <div>
            <label htmlFor="edit-ws-description" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Description
            </label>
            <textarea
              id="edit-ws-description"
              rows={3}
              value={editForm.description}
              onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            />
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={deactivateOpen}
        title="Deactivate workspace"
        message={`Deactivate "${workspace.name}"? Members lose access until it is reactivated. Nothing is deleted.`}
        confirmText="Deactivate"
        variant="warning"
        onConfirm={() => deactivateMutation.mutate()}
        onCancel={() => setDeactivateOpen(false)}
      />

      <ConfirmationDialog
        isOpen={reactivateOpen}
        title="Reactivate workspace"
        message={`Reactivate "${workspace.name}"? Members regain access with their previous roles.`}
        confirmText="Reactivate"
        variant="info"
        onConfirm={() => reactivateMutation.mutate()}
        onCancel={() => setReactivateOpen(false)}
      />

      <ConfirmationDialog
        isOpen={removeMemberTarget !== null}
        title="Remove member"
        message={
          removeMemberTarget
            ? `Remove ${removeMemberTarget.memberName} from "${workspace.name}"? The membership is archived, not deleted.`
            : ''
        }
        confirmText="Remove"
        variant="warning"
        onConfirm={() => removeMemberTarget && removeMemberMutation.mutate(removeMemberTarget.userId)}
        onCancel={() => setRemoveMemberTarget(null)}
      />

      <ConfirmationDialog
        isOpen={cancelInvitationTarget !== null}
        title="Remove invitation"
        message={
          cancelInvitationTarget
            ? `Remove the pending invitation for ${cancelInvitationTarget}?`
            : ''
        }
        confirmText="Remove Invitation"
        variant="warning"
        onConfirm={() => cancelInvitationTarget && cancelInvitationMutation.mutate(cancelInvitationTarget)}
        onCancel={() => setCancelInvitationTarget(null)}
      />
    </AdminPageShell>
  );
};

export default AccountWorkspaceDetailPage;
